/**
 * What this phone remembers about runs, so a weak signal (or a closed tab) never loses progress.
 *
 *   procedures.run.<runId>        LocalRunRecord: code, mode, start time and, for anonymous runs, the
 *                                 run token returned ONCE by startPublicRun (sent as X-Run-Token)
 *   procedures.inprogress.<code>  { runId }: the unfinished run for this procedure, so re-scanning the QR
 *                                 code offers "Carry on where you left off?"
 *   procedures.runview.<runId>    the last ProcedureRunView the server returned (offline start point)
 *   procedures.draft.<runId>      sub-checks ticked on a step that is not marked Done yet
 *
 * Signed-in runs also come back from the API (portal view resumeRunID), but the local record still
 * lets the runner open offline.
 */
import type { ProcedureRunView } from '@actschurch/ccms-sdk';
import type { PortalMode } from './queryKeys';
import { keysWithPrefix, readJson, removeKey, writeJson } from './storage';

export type RunState = 'active' | 'completed' | 'abandoned' | 'lost';

export interface LocalRunRecord {
  runId: number;
  code: string;
  mode: PortalMode;
  /** Anonymous runs only: the plain run token. */
  token?: string | null;
  /** Signed-in runs only: whose run this is (another account on the same phone never resumes it). */
  userId?: string | null;
  title?: string | null;
  runnerName?: string | null;
  startedAtMs: number;
  finishedAtMs?: number | null;
  state: RunState;
}

const runKey = (runId: number) => `procedures.run.${runId}`;
const inProgressKey = (code: string) => `procedures.inprogress.${code.toLowerCase()}`;
const runViewKey = (runId: number) => `procedures.runview.${runId}`;
const draftKey = (runId: number) => `procedures.draft.${runId}`;

export function getRunRecord(runId: number): LocalRunRecord | null {
  const r = readJson<LocalRunRecord>(runKey(runId));
  return r && typeof r.runId === 'number' ? r : null;
}

export function saveRunRecord(record: LocalRunRecord): void {
  writeJson(runKey(record.runId), record);
}

export function updateRunRecord(runId: number, patch: Partial<LocalRunRecord>): LocalRunRecord | null {
  const current = getRunRecord(runId);
  if (!current) return null;
  const next = { ...current, ...patch };
  saveRunRecord(next);
  return next;
}

export function getRunToken(runId: number): string | null {
  return getRunRecord(runId)?.token ?? null;
}

/** Remember the unfinished run for a procedure (one per procedure per phone). */
export function setInProgress(code: string, runId: number): void {
  writeJson(inProgressKey(code), { runId });
}

export function clearInProgress(code: string, runId?: number): void {
  const current = readJson<{ runId: number }>(inProgressKey(code));
  if (!current) return;
  if (runId === undefined || current.runId === runId) removeKey(inProgressKey(code));
}

/**
 * The unfinished run on this phone for `code` that the current person may resume: anonymous runs
 * always (they carry their token), signed-in runs only for the same account.
 */
export function getInProgress(code: string, userId: string | null): LocalRunRecord | null {
  const ref = readJson<{ runId: number }>(inProgressKey(code));
  if (!ref || typeof ref.runId !== 'number') return null;
  const record = getRunRecord(ref.runId);
  if (!record || record.state !== 'active') return null;
  if (record.mode === 'public' && !record.token) return null;
  if (record.mode === 'member' && (!userId || record.userId !== userId)) return null;
  return record;
}

/** Every unfinished run on this phone the current person may resume, newest first. */
export function listInProgress(userId: string | null): LocalRunRecord[] {
  const out: LocalRunRecord[] = [];
  for (const key of keysWithPrefix('procedures.inprogress.')) {
    const code = key.slice('procedures.inprogress.'.length);
    const record = getInProgress(code, userId);
    if (record) out.push(record);
  }
  return out.sort((a, b) => b.startedAtMs - a.startedAtMs);
}

export function markRunFinished(runId: number, state: Exclude<RunState, 'active'>): void {
  const record = updateRunRecord(runId, { state, finishedAtMs: Date.now() });
  if (record) clearInProgress(record.code, runId);
}

export function getRunSnapshot(runId: number): ProcedureRunView | null {
  return readJson<ProcedureRunView>(runViewKey(runId));
}

export function saveRunSnapshot(view: ProcedureRunView): void {
  writeJson(runViewKey(view.runID), view);
}

export type StepDrafts = Record<string, number[]>;

export function getDrafts(runId: number): StepDrafts {
  return readJson<StepDrafts>(draftKey(runId)) ?? {};
}

export function saveDraft(runId: number, stepId: number, checkedIds: number[]): void {
  const drafts = getDrafts(runId);
  drafts[String(stepId)] = checkedIds;
  writeJson(draftKey(runId), drafts);
}

/**
 * Housekeeping: forget finished runs older than 30 days (their token, snapshot and drafts).
 * Unfinished runs are kept until they are finished or replaced.
 */
export function pruneOldRuns(maxAgeDays = 30): void {
  const cutoff = Date.now() - maxAgeDays * 24 * 60 * 60 * 1000;
  for (const key of keysWithPrefix('procedures.run.')) {
    const r = readJson<LocalRunRecord>(key);
    if (!r) continue;
    const finished = r.state !== 'active';
    const stamp = r.finishedAtMs ?? r.startedAtMs;
    if (finished && stamp < cutoff) {
      removeKey(key);
      removeKey(runViewKey(r.runId));
      removeKey(draftKey(r.runId));
    }
  }
}
